import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Users, DollarSign, ShieldAlert, Plus, Search, MoreHorizontal, Trash2,
  Ban, RotateCcw, Loader2, Check, CalendarDays,
} from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { toast } from "sonner";
import { PageHeader } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const ROLES = ["Operations Manager", "Support Agent", "Finance Manager", "Marketing Manager"];
const PERMISSIONS = ["View employees", "Manage schedules", "View reports", "Manage payroll", "Manage access", "Manage promotions", "Manage support", "Wallet & Payments", "Full system access"];
const ROLE_PERMISSIONS = {
  "Operations Manager": ["Manage schedules", "Manage access"],
  "Support Agent": ["Manage support"],
  "Finance Manager": ["Manage payroll", "Wallet & Payments"],
  "Marketing Manager": ["Manage promotions"],
};
const STATUS_STYLE = {
  Active: "text-[#16a34a] bg-[#16a34a]",
  Invited: "text-[#2340b5] bg-[#2340b5]",
  Suspended: "text-[#e4002b] bg-[#e4002b]",
};
const input = "w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm text-[#12142b] outline-none focus:border-[#2340b5]";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const money = (v) => `$${Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const wageLabel = (e) => e.pay_type === "Monthly" ? `${money(e.hourly_rate)}/mo` : `${money(e.hourly_rate)}/hr`;
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Invited;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${s.split(" ")[0]}`} data-testid={`status-${status}`}>
      <span className={`h-2 w-2 rounded-full ${s.split(" ")[1]}`} /> {status}
    </span>
  );
}

function RoleBadge({ role }) {
  return <span className="rounded-md bg-[#f59e0b]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#b45309]">{role}</span>;
}

function Avatar({ name, size = "h-10 w-10 text-xs" }) {
  return <span className={`grid ${size} shrink-0 place-items-center rounded-full bg-[#2340b5]/10 font-bold text-[#2340b5]`}>{initials(name)}</span>;
}

export default function Employees() {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState({ active: 0, monthly_payroll: 0, attention: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [menuId, setMenuId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/employees");
      setItems(data.items || []);
      setStats(data.stats || {});
      setSelectedId((prev) => prev || (data.items || [])[0]?.id || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load employees");
      setItems([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => (items || []).filter((e) => {
    const q = query.trim().toLowerCase();
    if (q && ![e.name, e.email, e.phone].join(" ").toLowerCase().includes(q)) return false;
    if (roleFilter !== "all" && e.role !== roleFilter) return false;
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    return true;
  }), [items, query, roleFilter, statusFilter]);

  const selected = (items || []).find((e) => e.id === selectedId) || filtered[0] || null;

  const setStatus = async (emp, status) => {
    try {
      await adminApi.patch(`/employees/${emp.id}`, { status });
      toast.success(`${emp.name} ${status === "Suspended" ? "suspended" : status.toLowerCase()}`);
      setMenuId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const remove = async (emp) => {
    try {
      await adminApi.delete(`/employees/${emp.id}`);
      toast.success(`${emp.name} removed`);
      setMenuId(null);
      setSelectedId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div data-testid="employees-page">
      <PageHeader title="Employee Management" subtitle="Manage your team, wages, access and payroll."
        right={
          <button data-testid="add-employee-btn" onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-5 py-2.5 font-display text-sm font-bold text-white hover:bg-[#1b3390]">
            <Plus className="h-4 w-4" /> Add employee
          </button>
        } />
      <div className="space-y-6 p-8">
        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { icon: Users, label: "Active employees", value: stats.active, testid: "emp-stat-active" },
            { icon: DollarSign, label: "Monthly payroll", value: money(stats.monthly_payroll), testid: "emp-stat-payroll" },
            { icon: ShieldAlert, label: "Needs attention", value: stats.attention, testid: "emp-stat-attention" },
          ].map((c) => (
            <div key={c.testid} className="rounded-2xl border border-[#12142b]/8 bg-white p-5" data-testid={c.testid}>
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#2340b5]/8"><c.icon className="h-4.5 w-4.5 text-[#2340b5]" /></span>
              <p className="mt-3 font-display text-2xl font-bold text-[#12142b]">{c.value}</p>
              <p className="text-xs text-[#565b73]">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-[2fr_1fr]">
          {/* Table */}
          <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6">
            <h2 className="mb-4 font-display text-lg font-bold text-[#12142b]">Employees</h2>
            <div className="mb-4 grid gap-3 md:grid-cols-[1fr_auto_auto]">
              <div className="flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3">
                <Search className="h-4 w-4 text-[#565b73]" />
                <input data-testid="emp-search" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, email or phone" className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-[#565b73]/60" />
              </div>
              <select data-testid="emp-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={input + " md:w-44"}>
                <option value="all">All roles</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <select data-testid="emp-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={input + " md:w-40"}>
                <option value="all">All statuses</option>
                {Object.keys(STATUS_STYLE).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-[1.6fr_1fr_0.8fr_0.7fr_40px] gap-3 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73] max-md:hidden">
              <span>Employee</span><span>Role</span><span>Hourly wage</span><span>Status</span><span className="text-right">Actions</span>
            </div>
            {items === null ? (
              <p className="py-10 text-center text-sm text-[#565b73]">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="py-10 text-center text-sm text-[#565b73]" data-testid="emp-empty">No employees yet — add your first team member.</p>
            ) : filtered.map((e) => (
              <div key={e.id} data-testid={`emp-row-${e.id}`} onClick={() => setSelectedId(e.id)}
                className={`relative grid cursor-pointer grid-cols-[1.6fr_1fr_0.8fr_0.7fr_40px] items-center gap-3 border-b border-[#12142b]/6 py-3.5 last:border-0 hover:bg-[#f4f6fb]/60 ${selected?.id === e.id ? "bg-[#2340b5]/4" : ""}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={e.name} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[#12142b]">{e.name}</p>
                    <p className="truncate text-xs text-[#565b73]">{e.email}</p>
                  </div>
                </div>
                <div><RoleBadge role={e.role} /></div>
                <span className="text-sm font-semibold text-[#12142b]">{wageLabel(e)}</span>
                <StatusPill status={e.status} />
                <div className="text-right">
                  <button data-testid={`emp-menu-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setMenuId(menuId === e.id ? null : e.id); }}
                    className="rounded-lg p-1.5 text-[#565b73] hover:bg-[#12142b]/6"><MoreHorizontal className="h-4 w-4" /></button>
                  {menuId === e.id && (
                    <div className="absolute right-2 top-12 z-20 w-44 rounded-xl border border-[#12142b]/10 bg-white p-1.5 text-left shadow-xl" data-testid={`emp-menu-pop-${e.id}`}>
                      {e.status === "Suspended" ? (
                        <button data-testid={`emp-reactivate-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setStatus(e, "Active"); }}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[#16a34a] hover:bg-[#16a34a]/8"><RotateCcw className="h-3.5 w-3.5" /> Reactivate</button>
                      ) : (
                        <button data-testid={`emp-suspend-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setStatus(e, "Suspended"); }}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[#b45309] hover:bg-[#f59e0b]/10"><Ban className="h-3.5 w-3.5" /> Suspend</button>
                      )}
                      <button data-testid={`emp-delete-${e.id}`} onClick={(ev) => { ev.stopPropagation(); remove(e); }}
                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[#e4002b] hover:bg-[#e4002b]/8"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Details panel */}
          <DetailsPanel employee={selected} onChanged={load} onRemoveAccess={(e) => setStatus(e, "Suspended")} />
        </div>
      </div>

      <AddEmployeeDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={(emp) => { setAddOpen(false); setSelectedId(emp.id); load(); }} />
    </div>
  );
}

function DetailsPanel({ employee, onChanged, onRemoveAccess }) {
  const [tab, setTab] = useState("profile");
  const [payments, setPayments] = useState(null);
  const [rate, setRate] = useState("");
  const [payType, setPayType] = useState("Hourly");
  const [effDate, setEffDate] = useState("");
  const [savingWage, setSavingWage] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const loadPayments = useCallback(() => {
    if (!employee) return setPayments([]);
    adminApi.get(`/employees/${employee.id}/payments`).then((r) => setPayments(r.data.items || [])).catch(() => setPayments([]));
  }, [employee?.id]);
  useEffect(() => { loadPayments(); }, [loadPayments]);
  useEffect(() => {
    if (employee) {
      setRate(employee.hourly_rate ?? "");
      setPayType(employee.pay_type || "Hourly");
      setEffDate(employee.effective_date || new Date().toISOString().slice(0, 10));
    }
  }, [employee?.id]);

  if (!employee) {
    return (
      <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6 text-center text-sm text-[#565b73]" data-testid="emp-detail-panel">
        Select an employee to see details.
      </div>
    );
  }

  const saveWage = async () => {
    const v = parseFloat(rate);
    if (Number.isNaN(v) || v < 0) return toast.error("Enter a valid rate");
    setSavingWage(true);
    try {
      await adminApi.patch(`/employees/${employee.id}`, { hourly_rate: v, pay_type: payType, effective_date: effDate });
      toast.success("Wage saved");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save wage"); }
    finally { setSavingWage(false); }
  };

  const TABS = [["profile", "Profile"], ["wages", "Wages"], ["payhistory", "Pay history"], ["access", "Access"]];

  return (
    <div className="self-start rounded-2xl border border-[#12142b]/8 bg-white p-6" data-testid="emp-detail-panel">
      {/* header */}
      <div className="mb-4 flex items-start gap-3">
        <Avatar name={employee.name} size="h-12 w-12 text-sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate font-display text-base font-bold text-[#12142b]" data-testid="emp-detail-name">{employee.name}</p>
            <StatusPill status={employee.status} />
          </div>
          <p className="truncate text-xs text-[#565b73]">{employee.email}</p>
        </div>
      </div>

      {/* tabs */}
      <div className="mb-5 flex gap-4 border-b border-[#12142b]/8" data-testid="emp-detail-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`emp-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 pb-2 text-sm font-semibold ${tab === k ? "border-[#2340b5] text-[#2340b5]" : "border-transparent text-[#565b73] hover:text-[#12142b]"}`}>{l}</button>
        ))}
      </div>

      {tab === "profile" && (
        <div className="space-y-3" data-testid="emp-profile-tab">
          {[["Phone", employee.phone || "—"], ["Role", employee.role], ["Pay type", employee.pay_type],
            ["Effective date", fmtDate(employee.effective_date)], ["Added", fmtDate(employee.created_at)],
            ["SafeRide account", employee.maple_user_id ? "Linked — can sign in with own credentials" : "Not linked"]].map(([l, v]) => (
            <div key={l} className="flex items-center justify-between border-b border-[#12142b]/6 pb-2.5 text-sm last:border-0">
              <span className="text-[#565b73]">{l}</span><span className="font-semibold text-[#12142b]">{v}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "wages" && (
        <div data-testid="emp-wages-tab">
          <h3 className="mb-3 text-sm font-bold text-[#12142b]">Wage information</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">{payType === "Monthly" ? "Monthly wage" : "Hourly rate"}</label>
              <input data-testid="wage-rate-input" type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className={input} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">Pay type</label>
              <select data-testid="wage-pay-type" value={payType} onChange={(e) => setPayType(e.target.value)} className={input}>
                <option>Hourly</option><option>Monthly</option>
              </select>
            </div>
          </div>
          <label className="mb-1 mt-3 block text-xs font-semibold text-[#565b73]">Effective date</label>
          <div className="flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3">
            <CalendarDays className="h-4 w-4 text-[#565b73]" />
            <input data-testid="wage-effective-date" type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} className="w-full bg-transparent py-2.5 text-sm outline-none" />
          </div>
          <button data-testid="wage-save" onClick={saveWage} disabled={savingWage}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2340b5] py-3 font-display text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
            {savingWage && <Loader2 className="h-4 w-4 animate-spin" />} Save wage
          </button>

          <div className="mt-6 flex items-center justify-between">
            <h3 className="text-sm font-bold text-[#12142b]">Recent pay history</h3>
            <button data-testid="view-all-pay-history" onClick={() => setTab("payhistory")} className="text-xs font-semibold text-[#2340b5] hover:text-[#1b3390]">View all pay history →</button>
          </div>
          <div className="mt-2">
            {(payments || []).slice(0, 3).map((p) => <PayRow key={p.id} p={p} />)}
            {payments && payments.length === 0 && <p className="py-3 text-xs text-[#565b73]">No payments recorded yet.</p>}
          </div>
        </div>
      )}

      {tab === "payhistory" && (
        <div data-testid="emp-payhistory-tab">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-bold text-[#12142b]">Pay history</h3>
            <button data-testid="record-payment-btn" onClick={() => setPayOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#2340b5] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#1b3390]">
              <Plus className="h-3.5 w-3.5" /> Record payment
            </button>
          </div>
          {(payments || []).map((p) => <PayRow key={p.id} p={p} />)}
          {payments && payments.length === 0 && <p className="py-6 text-center text-sm text-[#565b73]">No payments yet — record the first one.</p>}
        </div>
      )}

      {tab === "access" && (
        <div data-testid="emp-access-tab">
          <h3 className="mb-3 text-sm font-bold text-[#12142b]">Access management</h3>
          <div className="mb-4 flex items-center gap-2 text-sm">
            <span className="text-[#565b73]">Assigned role</span><RoleBadge role={employee.role} />
          </div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#565b73]">Permissions</p>
          <div className="grid grid-cols-2 gap-y-2.5" data-testid="emp-permissions-list">
            {PERMISSIONS.map((perm) => {
              const granted = (ROLE_PERMISSIONS[employee.role] || []).includes(perm);
              return (
                <span key={perm} className={`flex items-center gap-1.5 text-xs ${granted ? "font-medium text-[#12142b]" : "text-[#565b73]/50 line-through"}`}>
                  <Check className={`h-3.5 w-3.5 ${granted ? "text-[#16a34a]" : "text-[#565b73]/30"}`} /> {perm}
                </span>
              );
            })}
          </div>
          <button data-testid="remove-access-btn" onClick={() => onRemoveAccess(employee)} disabled={employee.status === "Suspended"}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-[#e4002b]/40 py-2.5 text-sm font-bold text-[#e4002b] hover:bg-[#e4002b]/6 disabled:opacity-50">
            <Trash2 className="h-4 w-4" /> {employee.status === "Suspended" ? "Access removed" : "Remove access"}
          </button>
        </div>
      )}

      <PaymentDialog open={payOpen} employee={employee} onClose={() => setPayOpen(false)}
        onSaved={() => { setPayOpen(false); loadPayments(); onChanged(); }} />
    </div>
  );
}

function PayRow({ p }) {
  return (
    <div className="flex items-center justify-between border-b border-[#12142b]/6 py-2.5 text-sm last:border-0" data-testid={`pay-row-${p.id}`}>
      <span className="text-[#565b73]">{fmtDate(p.paid_on)}</span>
      <span className="font-semibold text-[#12142b]">{money(p.amount)}</span>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#16a34a]/10 px-2.5 py-0.5 text-xs font-semibold text-[#16a34a]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#16a34a]" /> {p.status}
      </span>
    </div>
  );
}

function AddEmployeeDialog({ open, onClose, onCreated }) {
  const [picked, setPicked] = useState(null); // directory user
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [form, setForm] = useState({ role: "Support Agent", hourly_rate: "", pay_type: "Hourly" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // debounced directory search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await adminApi.get("/employees/directory", { params: { q } });
        setResults(data.items || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  const submit = async () => {
    if (!picked) return toast.error("Pick a SafeRide user first");
    setSaving(true);
    try {
      const { data } = await adminApi.post("/employees", {
        maple_user_id: picked.id, role: form.role,
        hourly_rate: parseFloat(form.hourly_rate) || 0, pay_type: form.pay_type,
      });
      toast.success(`${data.employee.name} added — Active, they can sign in now`);
      setPicked(null); setQ(""); setResults(null);
      setForm({ role: "Support Agent", hourly_rate: "", pay_type: "Hourly" });
      onCreated(data.employee);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add employee"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-emp-dialog">
        <DialogHeader><DialogTitle>Add employee</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!picked ? (
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">Find their SafeRide account</label>
              <div className="flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3">
                <Search className="h-4 w-4 text-[#565b73]" />
                <input data-testid="emp-dir-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or email…" className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-[#565b73]/60" />
                {searching && <Loader2 className="h-4 w-4 animate-spin text-[#565b73]" />}
              </div>
              <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-[#12142b]/8" data-testid="emp-dir-results">
                {(results || []).map((u) => (
                  <button key={u.id} data-testid={`emp-dir-result-${u.id}`} disabled={u.already_employee}
                    onClick={() => setPicked(u)}
                    className="flex w-full items-center gap-3 border-b border-[#12142b]/6 px-3 py-2.5 text-left last:border-0 hover:bg-[#f4f6fb] disabled:opacity-45">
                    <Avatar name={u.name} size="h-8 w-8 text-[10px]" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-[#12142b]">{u.name}</span>
                      <span className="block truncate text-xs text-[#565b73]">{u.email}{u.phone ? ` · ${u.phone}` : ""}</span>
                    </span>
                    {u.already_employee
                      ? <span className="text-[10px] font-bold uppercase text-[#b45309]">Employed</span>
                      : <span className="text-[10px] font-bold uppercase text-[#2340b5]">{u.role}</span>}
                  </button>
                ))}
                {results && results.length === 0 && (
                  <p className="px-3 py-4 text-center text-xs text-[#565b73]">No SafeRide users match — they must sign up for SafeRide first.</p>
                )}
                {results === null && <p className="px-3 py-4 text-center text-xs text-[#565b73]">Type to search the users directory…</p>}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-xl border border-[#2340b5]/25 bg-[#2340b5]/5 p-3" data-testid="emp-selected-user">
              <Avatar name={picked.name} size="h-10 w-10 text-xs" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-[#12142b]">{picked.name}</p>
                <p className="truncate text-xs text-[#565b73]">{picked.email}{picked.phone ? ` · ${picked.phone}` : ""}</p>
              </div>
              <button data-testid="emp-clear-pick" onClick={() => setPicked(null)} className="text-xs font-semibold text-[#2340b5] hover:text-[#1b3390]">Change</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">Role</label>
              <select data-testid="add-emp-role" value={form.role} onChange={set("role")} className={input}>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">Pay type</label>
              <select data-testid="add-emp-paytype" value={form.pay_type} onChange={set("pay_type")} className={input}>
                <option>Hourly</option><option>Monthly</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-semibold text-[#565b73]">{form.pay_type === "Monthly" ? "Monthly wage ($)" : "Hourly rate ($)"}</label>
              <input data-testid="add-emp-rate" type="number" min="0" step="0.01" value={form.hourly_rate} onChange={set("hourly_rate")} placeholder="28.00" className={input} />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-2 gap-2 sm:justify-between">
          <button data-testid="add-emp-cancel" onClick={onClose} disabled={saving}
            className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Cancel</button>
          <button data-testid="add-emp-submit" onClick={submit} disabled={saving || !picked}
            className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Add employee
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ open, employee, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const v = parseFloat(amount);
    if (Number.isNaN(v) || v <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      await adminApi.post(`/employees/${employee.id}/payments`, { amount: v, paid_on: paidOn, note: note.trim() });
      toast.success("Payment recorded");
      setAmount(""); setNote("");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not record payment"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Record payment — {employee?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-[#565b73]">Amount ($)</label>
            <input data-testid="pay-amount-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={input} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-[#565b73]">Paid on</label>
            <input data-testid="pay-date-input" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className={input} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-[#565b73]">Note <span className="font-normal">(optional)</span></label>
            <input data-testid="pay-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. September payroll" className={input} />
          </div>
        </div>
        <DialogFooter className="mt-2 gap-2 sm:justify-between">
          <button data-testid="pay-cancel" onClick={onClose} disabled={saving}
            className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Cancel</button>
          <button data-testid="pay-submit" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save payment
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
